import { X, Building2, Briefcase, Shield, UserCog, CheckCircle2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AccountUser } from '../../../api/accounts';
import { WorkOSBadge, type WorkOSStatus } from '../../../components/shared/WorkOSBadge';

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface UserDetailPanelProps {
  user: AccountUser | null;
  onClose: () => void;
}

export default function UserDetailPanel({ user, onClose }: UserDetailPanelProps) {
  const navigate = useNavigate();

  if (!user) return null;

  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();

  const fields = [
    user.company_name && {
      icon: <Building2 size={15} />,
      label: 'Customer',
      content: user.company_id ? (
        <button
          onClick={() => { onClose(); navigate(`../cu/${user.company_id}`); }}
          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
        >
          {user.company_name}
        </button>
      ) : (
        <p className="text-sm text-neutral-900">{user.company_name}</p>
      ),
    },
    user.job_title && {
      icon: <Briefcase size={15} />,
      label: 'Job Title',
      content: <p className="text-sm text-neutral-900">{user.job_title}</p>,
    },
    {
      icon: <Shield size={15} />,
      label: 'WorkOS Status',
      content: <WorkOSBadge status={user.workos_status as WorkOSStatus} />,
    },
    {
      icon: <UserCog size={15} />,
      label: 'Role',
      content: <p className="text-sm text-neutral-900 capitalize">{user.membership_role}</p>,
    },
    {
      icon: <CheckCircle2 size={15} />,
      label: 'Email Verified',
      content: (
        <p className="text-sm text-neutral-900">
          {user.email_verified ? (
            <span className="text-emerald-600">Yes</span>
          ) : (
            <span className="text-neutral-400">No</span>
          )}
        </p>
      ),
    },
    {
      icon: <Clock size={15} />,
      label: 'Last Sign-In',
      content: <p className="text-sm text-neutral-900">{relativeTime(user.last_sign_in_at)}</p>,
    },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; content: React.ReactNode }[];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[400px] bg-white shadow-2xl border-l border-neutral-200 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <h2 className="text-sm font-semibold text-neutral-900">User Details</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Profile */}
        <div className="px-5 py-6 flex flex-col items-center border-b border-neutral-100">
          {user.profile_picture_url ? (
            <img
              src={user.profile_picture_url}
              alt={`${user.first_name} ${user.last_name}`}
              className="w-16 h-16 rounded-full object-cover mb-3 ring-2 ring-neutral-100"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-lg font-semibold mb-3 ring-2 ring-blue-100">
              {initials}
            </div>
          )}
          <p className="text-lg font-semibold text-neutral-900">
            {user.first_name} {user.last_name}
          </p>
          <p className="text-sm text-neutral-500">{user.email}</p>
        </div>

        {/* Details */}
        <div className="px-5 py-4 space-y-4">
          {fields.map((field, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-neutral-400 mt-0.5">{field.icon}</span>
              <div>
                <p className="text-xs text-neutral-400 mb-0.5">{field.label}</p>
                {field.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
